/**
 * Link d'invito del gruppo: mostra (su richiesta), copia, reimposta con conferma.
 * WhatsApp da' il link solo agli amministratori.
 */
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link2, Copy, RefreshCw, Loader2 } from 'lucide-react';
import { useApp } from '@/components/AppProvider';
import { tip } from '@/components/messages/ChatTooltip';

interface Props {
  canManage: boolean;
  disabledReason?: string | null;
  busy?: boolean;
  getInvite: () => Promise<string | null>;
  revokeInvite: () => Promise<string | null | undefined>;
}

export function GroupInviteSection({ canManage, disabledReason, busy, getInvite, revokeInvite }: Props) {
  const { confirm } = useApp();
  const [link, setLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const show = async () => {
    setLoading(true);
    try {
      const l = await getInvite();
      if (l) setLink(l);
      else toast.error('WhatsApp non ha restituito il link d\'invito.');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Link d\'invito non disponibile.');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiato');
    } catch {
      toast.error('Copia non riuscita: seleziona il link e copialo a mano.');
    }
  };

  const reset = async () => {
    const ok = await confirm({
      title: 'Reimpostare il link d\'invito?',
      message: 'Il link attuale smetterà di funzionare: chi lo ha già ricevuto non potrà più entrare con quello. Ne verrà creato uno nuovo.',
      confirmText: 'Reimposta link',
      cancelText: 'Annulla',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    const nuovo = await revokeInvite();
    if (nuovo) setLink(nuovo);
    else if (nuovo === null) setLink(null);
  };

  const btn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/10 text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10 space-y-2">
      <h4 className="flex items-center gap-1.5 text-xs text-[#787774] uppercase tracking-wide"><Link2 className="w-3.5 h-3.5" />Link d'invito</h4>
      {link ? (
        <>
          <p className="text-xs text-[#2383E2] break-all select-all">{link}</p>
          <div className="flex gap-2">
            <button type="button" className={btn} onClick={() => void copy()}><Copy className="w-3.5 h-3.5" />Copia</button>
            <button type="button" className={btn} onClick={() => void reset()} disabled={busy}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Reimposta
            </button>
          </div>
        </>
      ) : (
        <button type="button" className={btn} onClick={() => void show()} disabled={!canManage || loading}
          {...tip(canManage ? 'Chi apre il link può entrare nel gruppo' : disabledReason || 'Solo gli amministratori vedono il link')}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}Mostra link d'invito
        </button>
      )}
    </div>
  );
}
